import { DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import {
	getAnonymizedEventName,
	getAnonymizedHumanName,
	getAnonymizedProductName,
	StoreToPortalFunctionality,
	StoreToPortalMap,
	transactionSpanWrapper
} from '@/common/utils'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useScorePeriods } from '@/features/saleDaysSummary/composables/useScorePeriods'
import { type PeriodsDropdown, type SaleDaysSummaryData, type SaleDaysSummaryDataForUI } from '@/features/saleDaysSummary/types'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { fetchSaleDaysSummary } from '@/features/stores/storesRemoteRepository'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import { type Portal } from '@/types'

const defaultSummary: SaleDaysSummaryDataForUI = {
	score: 0,
	stores: [],
	startDate: DateTime.now(),
	endDate: DateTime.now()
}

const useSaleDaysSummaryStoreInner = defineStore('saleDaysSummaryInner', () => {
	const summary = ref<SaleDaysSummaryDataForUI>(defaultSummary)
	const isLoading = ref(false)

	return {
		summary,
		isLoading
	}
})

export const useSaleDaysSummaryStore = defineStore('saleDaysSummary', () => {
	const innerStore = useSaleDaysSummaryStoreInner()
	const { saleDaysSummaryPeriod } = storeToRefs(usePreferencesStore())
	const { periods, defaultPeriod, defaultStartDate, defaultEndDate } = useScorePeriods(saleDaysSummaryPeriod)
	const { startTransaction, trackEvent } = useTelemetry()
	const { stores, storeIdToStoreName } = storeToRefs(useStoresStore())
	const { favouriteSKUIdsByStore } = storeToRefs(useSkusStore())
	const { shouldAnonymizeData } = storeToRefs(useFeatureFlagStore())
	const summary = computed(() => innerStore.summary)
	const isLoading = computed(() => innerStore.isLoading)
	const selectedPeriod = computed(() => periods.value.items?.find(({ selected }) => selected)?.data?.period)
	const lastFetchedPeriod = ref()
	const { setSaleDaysSummaryPeriod } = usePreferencesStore()
	const summaryFetchAbortController = ref<AbortController>()
	const storesWithSummaryIds = computed(() =>
		stores.value.filter((store) => StoreToPortalFunctionality.get(store.id)?.has('scheduling')).map(({ id }) => id)
	)

	function rawDataToUIData(rawData: SaleDaysSummaryData): SaleDaysSummaryDataForUI {
		return {
			...rawData,
			stores: rawData.stores.map((store) => {
				if (shouldAnonymizeData.value) {
					store.products.forEach((product) => {
						product.name = getAnonymizedProductName(product.name)

						product.skus.forEach((sku) => {
							sku.humanName = getAnonymizedHumanName(product.name)

							sku.actualTimeline.forEach((event) => {
								event.name = getAnonymizedEventName(event.name)
							})
						})
					})
				}

				return {
					...store,
					portal: (StoreToPortalMap.get(store.storeName) ?? store.storeName) as Portal,
					name: storeIdToStoreName.value[store.storeName] ?? store.storeName
				}
			})
		}
	}

	async function fetch() {
		if (innerStore.isLoading) {
			summaryFetchAbortController.value?.abort()

			return
		}

		if (storesWithSummaryIds.value.length === 0) {
			trackEvent('app', 'Aborted loading sale days summary, because there are no stores.')

			return
		}

		try {
			innerStore.isLoading = true

			summaryFetchAbortController.value = new AbortController()

			lastFetchedPeriod.value = selectedPeriod.value ?? defaultPeriod

			const matchingPeriodData = periods.value.items?.find(({ data }) => data.period === lastFetchedPeriod.value)?.data

			const startDate = matchingPeriodData?.startDate ?? defaultStartDate
			const endDate = matchingPeriodData?.endDate ?? defaultEndDate

			trackEvent('app', 'Start loading sale days summary', {
				storeName: storesWithSummaryIds.value.join(','),
				startDate: startDate.toISO(),
				endDate: endDate.toISO()
			})

			const transaction = startTransaction('saleDaysSummary', 'loading')

			await transactionSpanWrapper(
				async () => {
					const data = await fetchSaleDaysSummary(
						storesWithSummaryIds.value,
						startDate,
						endDate,
						favouriteSKUIdsByStore.value,
						summaryFetchAbortController.value?.signal
					)

					innerStore.summary = data ? rawDataToUIData(data) : defaultSummary
				},
				'summary',
				transaction
			)

			trackEvent('app', 'Loaded sale days summary', {
				storeName: storesWithSummaryIds.value.join(','),
				startDate: startDate.toISO(),
				endDate: endDate.toISO()
			})

			transaction?.end()
		} finally {
			innerStore.isLoading = false

			if (summaryFetchAbortController.value?.signal.aborted) {
				// If last fetch was aborted then we need to retry it
				summaryFetchAbortController.value = undefined

				fetch()
			} else {
				summaryFetchAbortController.value = undefined
			}
		}
	}

	function onPeriodChange(data: PeriodsDropdown) {
		// Change handler may be triggered too eagerly sometimes, so before we reload the data
		// we first check if the year was actually changed compared to what we have fetched last time
		const selectedPeriod = data.items?.[0]?.data?.period

		if (selectedPeriod) {
			setSaleDaysSummaryPeriod(selectedPeriod)
		}

		if (selectedPeriod && selectedPeriod !== lastFetchedPeriod.value) {
			fetch()
		}
	}

	return {
		fetch,
		onPeriodChange,
		summary,
		isLoading,
		periods,
		selectedPeriod,
		storesWithSummaryIds
	}
})
